import {AgentSelector} from '@github-ui/agent-sessions/components/new-task-chat-input/AgentSelector'
import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {newRepoPath} from '@github-ui/paths'
import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {GitBranchCheckIcon, GitBranchIcon, PlusIcon} from '@primer/octicons-react'
import {IconButton, SelectPanel, SkeletonBox} from '@primer/react'
import {type RefObject, useCallback, useMemo, useRef} from 'react'

import {copilotLocalStorage} from '../../utils/copilot-local-storage'
import {useChatStateValue} from '../../utils/CopilotChatContext'
import {IconButtonSelector} from './IconButtonSelector'
import {ImageAttachButton} from './ImageAttachButton'
import styles from './TaskChatInputToolbar.module.css'

type TaskChatInputToolbarProps = {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  selectedRepo: AgentDropdownRepository | undefined
  selectedBranch: string | undefined
  selectedAgent: SelectedAgent

  onRepoSelect: (repo: AgentDropdownRepository | undefined) => void
  onBranchSelect: (branch: string | undefined) => void
  onAgentSelect: (agent: SelectedAgent) => void
  agentSelectionEnabled?: boolean
  iconOnly?: boolean
  /** Ref to the textarea for image attachment insertion. When provided, renders the ImageAttachButton. */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** Whether the image limit has been reached. */
  isAtImageLimit?: boolean
  /** The current prompt text from the input. Used to pre-fill the new repository URL. */
  currentPrompt?: string
}

export const TaskChatInputToolbar = ({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  selectedRepo,
  selectedBranch,
  selectedAgent,
  onRepoSelect,
  onBranchSelect,
  onAgentSelect,
  agentSelectionEnabled = true,
  iconOnly = false,
  textAreaRef,
  isAtImageLimit,
  currentPrompt,
}: TaskChatInputToolbarProps) => {
  // Track whether we've already done the initial auto-selection
  const hasAutoSelectedRef = useRef(false)
  const currentUserLogin = useChatStateValue('currentUserLogin')

  const onLoad = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      // Only auto-select on the initial load when no repo is selected and we haven't auto-selected before
      let initialRepo: AgentDropdownRepository | undefined
      if (!selectedRepo && !hasAutoSelectedRef.current) {
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo && initialRepo.enabled) {
            onRepoSelect(initialRepo)
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo && loadedLastUsedRepo.enabled) {
            onRepoSelect(loadedLastUsedRepo)
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }

        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onRepoSelect(firstEnabledRepo)
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        } else if (initialRepo) {
          // The initial repo is disabled, but we have no enabled choices
          onRepoSelect(initialRepo)
          onBranchSelect(initialBranch || initialRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [selectedRepo, initialRepoNWO, onRepoSelect, onBranchSelect, initialBranch],
  )

  const repositoryLoader = useCallback(
    (query: string, defaultOwner?: string) => queryForRepos(query, defaultOwner, initialRepoNWO),
    [initialRepoNWO],
  )

  const newRepoUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (currentUserLogin) {
      params.set('owner', currentUserLogin)
    }
    if (currentPrompt?.trim()) {
      params.set('prompt', currentPrompt.trim())
    }
    const query = params.toString()
    return query ? `${newRepoPath()}?${query}` : newRepoPath()
  }, [currentUserLogin, currentPrompt])

  return (
    <ConsecutiveCAPI401sProvider>
      <div className={styles.container}>
        {!hideRepoSelector && (
          <ReposSelector<AgentDropdownRepository>
            repositoryLoader={repositoryLoader}
            selectionVariant="single"
            iconOnly={copilotFeatureFlags.askModeDropdown && iconOnly}
            responsiveStyle={copilotFeatureFlags.askModeDropdown ? 'icon-on-mobile' : 'full'}
            selectAllOption={false}
            onSelect={repo => {
              if (repo) {
                onRepoSelect(repo)
                onBranchSelect(repo.defaultBranch)
              }
            }}
            currentSelection={selectedRepo}
            width="medium"
            additionalButtonProps={{
              'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
              className: styles.repoSelectorButton,
              variant: 'invisible',
            }}
            onLoad={onLoad}
            loadingElement={<SkeletonBox width="150px" height="32px" />}
            secondaryAction={
              <SelectPanel.SecondaryActionLink
                href={newRepoUrl}
                leadingVisual={PlusIcon}
                onClick={() => {
                  sendEvent('agent_sessions.task_view', {
                    target: 'NEW_REPOSITORY_LINK',
                  })
                }}
              >
                New repository
              </SelectPanel.SecondaryActionLink>
            }
          />
        )}
        <>
          {!selectedRepo ? (
            <IconButton icon={GitBranchIcon} aria-label="Loading branches…" disabled />
          ) : (
            <RefSelector
              // `cacheKey` is something consumers of this component usually get from a key derived from a current repository in the app payload
              // In our case, it's a static string since this page is not aware of any kind of current repository
              cacheKey="repo-branch-agent-selector"
              canCreate={false}
              currentCommitish={selectedBranch ?? selectedRepo.defaultBranch}
              defaultBranch={selectedRepo.defaultBranch}
              owner={selectedRepo.owner}
              repo={selectedRepo.nameOnly}
              types={['branch']}
              selectedRefType="branch"
              closeOnSelect
              hideShowAll
              actionListHeadingText="Select a branch"
              onSelectItem={onBranchSelect}
              renderAnchor={props => (
                <IconButtonSelector
                  {...props}
                  icon={GitBranchIcon}
                  selectedIcon={GitBranchCheckIcon}
                  selected={selectedBranch !== selectedRepo.defaultBranch}
                  aria-label={`Branch: ${selectedBranch || 'Select branch'}`}
                  aria-labelledby={undefined}
                  variant="invisible"
                  className={styles.invisibleSelectorButton}
                />
              )}
            />
          )}
        </>

        {agentSelectionEnabled &&
          (!selectedRepo ? (
            <SkeletonBox width="32px" height="32px" />
          ) : (
            <AgentSelector
              repo={selectedRepo}
              branch={selectedBranch || selectedRepo.defaultBranch}
              selectedAgent={selectedAgent}
              onSelectAgent={agent => onAgentSelect(agent)}
              iconOnly
              buttonProps={{variant: 'invisible', className: styles.invisibleSelectorButton}}
            />
          ))}

        <ImageAttachButton
          textAreaRef={textAreaRef}
          repositoryId={selectedRepo?.id?.toString()}
          disabled={!selectedRepo}
          isAtImageLimit={isAtImageLimit}
        />
      </div>
    </ConsecutiveCAPI401sProvider>
  )
}

const COPILOT_REPOSITORIES_API_ENDPOINT = '/copilot/agent-sessions/repositories'

interface AgentRepositoryResponse {
  id: number
  graphQLId: string
  nameWithOwner: string
  enabled: boolean
  name: string
  owner: string
  defaultBranch: string
  visibility: string
  isEmpty: boolean
}

/**
 * Shared function to fetch Copilot-enabled repositories from the API
 */
export async function queryForRepos(
  query: string = '',
  defaultOwner?: string,
  repoToIncludeInTopRepos?: string,
): Promise<AgentDropdownRepository[]> {
  const url = new URL(COPILOT_REPOSITORIES_API_ENDPOINT, window.location.origin)

  if (query.trim()) {
    url.searchParams.set('q', query.trim())
  }

  if (defaultOwner) {
    url.searchParams.set('defaultOwner', defaultOwner)
  }

  if (repoToIncludeInTopRepos) {
    url.searchParams.set('includeInTopRepos', repoToIncludeInTopRepos)
  }

  const response = await verifiedFetchJSON(url.href)

  if (response.ok) {
    const {repositories} = (await response.json()) as {repositories: AgentRepositoryResponse[]}
    return repositories.map((repo: AgentRepositoryResponse) => {
      return {
        id: repo.id,
        graphQLId: repo.graphQLId,
        nameWithOwner: repo.nameWithOwner,
        enabled: repo.enabled,
        name: repo.nameWithOwner,
        nameOnly: repo.name,
        owner: repo.owner,
        defaultBranch: repo.defaultBranch,
        private: repo.visibility !== 'public',
        isEmpty: repo.isEmpty,
      }
    })
  }
  return []
}
